
// frontend/smartdocs-admin/src/components/ResponsiveTable.tsx

import React from 'react';

// Define a type for column configuration for better type safety with TypeScript
interface Column<T = any> {
  key: string;
  header: React.ReactNode;
  render?: (value: any, row: T) => React.ReactNode;
  removeSmall?: boolean;
}

interface ResponsiveTableProps<T = any> {
  columns: Column<T>[];
  data: T[];
  keyField?: string;
}

/**
 * A responsive table component that transforms into a card/list view on smaller screens.
 */
const ResponsiveTable = <T extends Record<string, any>>({
  columns,
  data,
  keyField = 'id'
}: ResponsiveTableProps<T>) => {
  if (!data || data.length === 0) {
    return <p className="text-center text-gray-500 py-4">No data available.</p>;
  }

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, rowIndex) => (
              <tr key={row[keyField] || rowIndex}>
                {columns.map((column) => (
                  <td key={`${row[keyField] || rowIndex}-${column.key}`} className="px-2 py-4 whitespace-pre-wrap break-words text-sm text-gray-900">
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
        <div className="md:hidden space-y-4">
        {data.map((row, rowIndex) => (
          <div key={row[keyField] || rowIndex}
               className="bg-white shadow overflow-hidden sm:rounded-lg p-4 border border-gray-200">
            {columns.map((column) => (
              !column.removeSmall ?
              (<div key={`${row[keyField] || rowIndex}-card-${column.key}`}
                   className="flex justify-between py-2 border-b last:border-b-0 border-gray-100">
                <span className="pr-3 text-sm font-medium text-gray-500">{column.header}</span>
                <span className="text-sm text-gray-900">
                {column.render ? column.render(row[column.key], row) : row[column.key]}
              </span>
              </div>) : null
            ))}
          </div>
        ))}
      </div>
    </>
  );
};

export default ResponsiveTable;
